import * as TSU from "@panyam/tsutils";
import { Entity, CmdParam, ParamsMixin } from "./entity";
import { Cycle } from "./cycle";
import { Line } from "./core";
import {
  RoleDef,
  RawBlock,
  BlockItem,
  Block,
  SectionBlock,
  RepeatBlock,
  CycleBlock,
  BeatDurationBlock,
  BreaksBlock,
  RoleBlock,
  GroupBlock,
  isBlock,
  isLine,
  isRawBlock,
  findContainingBlock,
} from "./block";

// Re-export for backward compatibility
export {
  RoleDef,
  RawBlock,
  BlockItem,
  Block,
  SectionBlock,
  RepeatBlock,
  CycleBlock,
  BeatDurationBlock,
  BreaksBlock,
  RoleBlock,
  GroupBlock,
  isBlock,
  isLine,
  isRawBlock,
  findContainingBlock,
};

// Re-export CmdParam for backwards compatibility
export { CmdParam } from "./entity";

// Apply ParamsMixin to Entity for Command base class
const EntityWithParams = ParamsMixin(Entity);

/**
 * Base class for commands in the notation.
 * Commands modify the notation in various ways, such as adding content,
 * changing layout, setting roles, etc.
 */
export abstract class Command extends EntityWithParams {
  /** Whether this command was auto-generated */
  autoGenerated = false;

  /** Previous command in the sequence */
  prevSibling: null | Command = null;

  /** Next command in the sequence */
  nextSibling: null | Command = null;

  /** Index of this command in the sequence */
  index: number;

  /**
   * Creates a new Command.
   * @param params Optional parameters for the command
   */
  constructor(params: CmdParam[] = []) {
    super();
    this.params = params;
    this.index = 0;
    this.validateParams();
  }

  /**
   * Validates the parameters for this command.
   * @throws Error if parameters are invalid
   */
  validateParams(): void {
    //
  }

  /**
   * Gets the name of this command.
   */
  get name(): string {
    return this.constructor.name;
  }

  /**
   * Returns a debug-friendly representation of this command.
   * @returns An object containing debug information
   */
  debugValue(): any {
    return { name: this.name, index: this.index, params: this.params };
  }

  /**
   * Applies this command to a block (the primary method).
   * Since Notation extends Block, this works for both.
   * @param container The block to apply this command to
   */
  abstract applyToBlock(container: Block): void;

  /**
   * @deprecated Use applyToBlock instead
   * Applies this command to a notation.
   * Default implementation delegates to applyToBlock.
   * @param notation The notation to apply this command to
   */
  applyToNotation(notation: Notation): void {
    this.applyToBlock(notation);
  }
}

/**
 * Represents metadata for the notation.
 */
export class MetaData {
  /**
   * Creates a new MetaData.
   * @param key The key for this metadata
   * @param value The value of this metadata
   * @param params Optional additional parameters
   */
  constructor(
    public readonly key: string,
    public readonly value: string,
    public readonly params?: any,
  ) {
    params = params || {};
  }
}

/**
 * The main class representing a complete notation.
 * Notation is the root Block of the notation hierarchy.
 * It contains all the elements, settings, and layout information for a piece of music.
 */
export class Notation extends Block {
  readonly TYPE = "Notation";

  // ============================================
  // Notation-specific properties
  // ============================================

  /** Metadata associated with this notation */
  metadata = new Map<string, MetaData>();

  /** Handler for missing roles */
  onMissingRole: (name: string) => RoleDef | null = (name) => this.newRoleDef(name, name == "sw");

  /**
   * Creates a new Notation.
   */
  constructor() {
    super("notation", null);
    // Set default values for root notation
    this.localCycle = Cycle.DEFAULT;
    this.localAtomsPerBeat = 1;
    this.localBreaks = [];
  }

  // ============================================
  // Backward compatibility aliases
  // ============================================

  /**
   * @deprecated Use blockItems instead
   * Legacy accessor for blocks array.
   */
  get blocks(): BlockItem[] {
    return this.blockItems;
  }

  /**
   * @deprecated Use localAtomsPerBeat instead
   * Legacy accessor for current atoms per beat.
   */
  get currentAPB(): number {
    return this.localAtomsPerBeat ?? 1;
  }

  set currentAPB(value: number) {
    this.localAtomsPerBeat = value;
  }

  /**
   * @deprecated Use localCycle instead
   * Legacy accessor for current cycle.
   */
  get currentCycle(): Cycle {
    return this.localCycle ?? Cycle.DEFAULT;
  }

  set currentCycle(value: Cycle) {
    this.localCycle = value;
  }

  /**
   * @deprecated Use localBreaks instead
   * Legacy accessor for current breaks.
   */
  get currentBreaks(): number[] {
    return this.localBreaks ?? [];
  }

  set currentBreaks(value: number[]) {
    this.localBreaks = value;
  }

  /**
   * @deprecated Use localRoles instead
   * Legacy accessor for roles array.
   */
  get roles(): RoleDef[] {
    return Array.from(this.localRoles.values());
  }

  // ============================================
  // Overridden methods
  // ============================================

  /**
   * Sets the current role by name.
   * Uses onMissingRole callback for auto-creation of missing roles.
   * @param name The name of the role to set as current
   * @throws Error if the name is empty or the role is not found
   */
  setCurrRole(name: string): void {
    name = name.trim().toLowerCase();
    if (name.trim() == "") {
      throw new Error("Role name cannot be empty");
    }
    const roleDef = this.getRole(name) || (this.onMissingRole ? this.onMissingRole(name) || null : null);
    if (roleDef == null) {
      throw new Error("Role not found: " + name);
    }
    this._currRoleDef = roleDef;
  }

  /**
   * Gets a role definition by name.
   * Handles empty name by returning the last added role.
   * @param name The name of the role
   * @returns The role definition, or null if not found
   */
  getRoleDef(name: string): TSU.Nullable<RoleDef> {
    name = name.trim().toLowerCase();
    if (name === "") {
      // Return the last added role if name is empty
      const roles = Array.from(this.localRoles.values());
      return roles[roles.length - 1] || null;
    }
    return this.localRoles.get(name) ?? null;
  }

  /**
   * Creates a new role definition.
   * @param name The name of the role
   * @param notesOnly Whether the role contains only notes, defaults to false
   * @returns The created role definition
   * @throws Error if the name is empty or the role already exists
   */
  newRoleDef(name: string, notesOnly = false): RoleDef {
    name = name.trim().toLowerCase();
    if (name === "") {
      throw new Error("Role name cannot be empty");
    }
    if (this.localRoles.has(name)) {
      throw new Error("Role already exists: " + name);
    }
    // create new and add
    const rd = new RoleDef();
    rd.name = name;
    rd.notesOnly = notesOnly;
    rd.index = this.localRoles.size;
    this.localRoles.set(name, rd);

    return rd;
  }

  // ============================================
  // Additional methods
  // ============================================

  /**
   * Adds a line to this notation.
   * @param line The line to add
   */
  addLine(line: Line): void {
    this.addBlockItem(line);
  }

  /**
   * Removes a line from this notation.
   * @param line The line to remove
   * @returns The index of the removed line, or -1 if not found
   */
  removeLine(line: Line): number {
    return this.removeBlockItem(line);
  }

  /**
   * Adds a raw block to this notation.
   * @param raw The raw block to add
   */
  addRawBlock(raw: RawBlock): void {
    this.addBlockItem(raw);
    this.resetLine();
  }

  /**
   * Adds metadata to this notation.
   * @param meta The metadata to add
   * @param addBlock Whether to add a corresponding raw block, defaults to true
   */
  addMetaData(meta: MetaData, addBlock = true): void {
    if (addBlock && !this.metadata.has(meta.key)) {
      // Add a new raw block here
      // set this by key so even if metadata changes we can
      // get latest value of it
      const raw = new RawBlock(meta.key, "metadata");
      this.addRawBlock(raw);
    }
    this.metadata.set(meta.key, meta);
  }

  /**
   * Resets the current line pointer to null.
   */
  resetLine(): void {
    this._currentLine = null;
  }

  /**
   * Returns a debug-friendly representation of this notation.
   * Uses Notation-specific property names for backward compatibility.
   * @returns An object containing debug information
   */
  debugValue(): any {
    // Use Notation-specific names (blocks, currentCycle, etc.)
    // instead of Block names (blockItems, localCycle, etc.)
    // to maintain backward compatibility with existing tests
    return {
      blocks: this.blocks.map((b) => b.debugValue()),
      currentAPB: this.currentAPB,
      currentBreaks: this.currentBreaks,
      currentCycle: this.currentCycle?.uuid,
      roles: this.roles,
    };
  }

  // Need protected access to _currRoleDef for setCurrRole override
  protected _currRoleDef: TSU.Nullable<RoleDef> = null;

  /**
   * Gets the current role definition.
   */
  get currRoleDef(): RoleDef | null {
    if (this._currRoleDef == null) {
      const roles = Array.from(this.localRoles.values());
      if (roles.length === 0) {
        return null;
      } else {
        this._currRoleDef = roles[roles.length - 1];
      }
    }
    return this._currRoleDef;
  }

  // Need protected access to _currentLine for resetLine
  protected _currentLine: Line | null = null;

  /**
   * Gets the current line, creating it if needed.
   */
  get currentLine(): Line {
    if (this._currentLine == null) {
      return this.newLine();
    }
    return this._currentLine;
  }

  /**
   * Creates a new line and makes it the current line.
   * @returns The newly created line
   */
  newLine(): Line {
    if (this._currentLine && this._currentLine.isEmpty) {
      // then remove it first instead of adding another
      // so we dont have a string of empty lines
      this.removeLine(this._currentLine);
    }
    this._currentLine = new Line();
    this.addLine(this._currentLine);
    return this._currentLine;
  }
}
